const ID_SEGMENT = /^[0-9A-Za-z._-]+$/;
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The bare Binary id behind whatever `attachment.url` happens to hold.
 *
 * Medplum rewrites the stored `Binary/{id}` into a presigned URL on every read,
 * search included, and that URL expires in an hour. The data-store is a long-lived
 * cache of rows that never change, so a synced URL goes stale and is never
 * refreshed — the UI mints a fresh one at click time from this id instead.
 *
 * Parsed by PATH SHAPE, never by host, and the shape differs per deployment:
 * cloud presigns as `<base>/binary/{binaryId}/{versionId}`, local as
 * `http://localhost:8103/storage/{binaryId}/{versionId}` with NO `binary`
 * segment at all. Both carry TWO ids and the binary's is the FIRST, so the
 * trailing uuid pair is the fallback when there is no marker to anchor on.
 */
export function binaryIdFromAttachmentUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  // Matched exactly as `patient-document.codec.ts` matches it — the two parses
  // cannot be shared (data-store must not import from the api package), so they
  // are kept spelling-identical instead.
  const segments = url
    .replace(/[?#].*$/, "")
    .split("/")
    .filter(Boolean);
  const anchor = segments.map((segment) => segment.toLowerCase()).lastIndexOf("binary");
  const version = segments[segments.length - 1];
  const penultimate = segments[segments.length - 2];
  const unmarkedPresigned = version && penultimate && UUID.test(version) && UUID.test(penultimate);
  const id = anchor === -1 ? (unmarkedPresigned ? penultimate : undefined) : segments[anchor + 1];
  return id && ID_SEGMENT.test(id) ? id : undefined;
}
